import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import theme from '../../assets/theme';
import CustomPagination from './components/CustomPagination';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Date utility functions
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [day, month, year].join('-');
};

const isToday = (date) => {
  if (!date) return false;
  return formatDate(date) === formatDate(new Date());
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead >
      <TableRow
        sx={{
          display: 'flex',
          backgroundColor: theme.palette.secondary.main,
          borderRadius: "8px",
          minWidth: "100vw",
          '&:hover': {
            backgroundColor: '#E1EFFF!important', // 🔵 Set hover color here
          },
          "& th:first-of-type": {
            borderBottomLeftRadius: "8px",
            borderTopLeftRadius: "8px",
            position: 'sticky',
            left: 0,
            zIndex: 3,
            backgroundColor: theme.palette.secondary.main,
          },
          "& th:nth-of-type(2)": {
            position: 'sticky',
            left: props.headCells[0]?.minWidth || '120px',
            zIndex: 3,
            backgroundColor: theme.palette.secondary.main,
          },
          "& th:last-of-type": {
            borderBottomRightRadius: "8px",
            borderTopRightRadius: "8px"
          },
        }}
      >
        {props.headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding ? headCell.padding : "none"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              flex: headCell.flex || 1,
              minWidth: headCell.minWidth || '120px',
              padding: '16px',
              backgroundColor: theme.palette.secondary.main,
              borderBottom: "none",
              justifyContent: headCell.align || "center",
              display: 'flex',
              alignItems: 'center',


            }}
          >
            {headCell.canSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                <Typography noWrap fontWeight={"500"} fontSize={14}>{headCell.label}</Typography>
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <Typography noWrap sx={{
                fontWeight: 700,
                fontSize: '14px',
                fontFamily: 'Nunito Sans',
              }}>{headCell.label}</Typography>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function BasicTable({ data, onRowClick, disableElevation, search = "", backgroundColor, columnSearch = {}, paginate = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(null);
  const [selected, setSelected] = React.useState([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = React.useState(search);
  const [searchArguments, setSearchArguments] = React.useState(columnSearch);

  React.useEffect(() => {
    if (data.columns.length != totalItems) {
      setPage(0);
      setTotalItems(data.columns.length);
      if (!paginate) {
        setRowsPerPage(data.columns.length);
      }
    }
  }, [data, setTotalItems, setPage]);

  React.useEffect(() => {
    const savedRowsPerPage = localStorage.getItem("rowsPerPage");
    const savedPage = localStorage.getItem("page");

    if (savedRowsPerPage) {
      setRowsPerPage(parseInt(savedRowsPerPage, 10));
    }
    if (savedPage) {
      setPage(parseInt(savedPage, 10));
    }
  }, []);

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    localStorage.setItem("rowsPerPage", newRowsPerPage);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    localStorage.setItem("page", newPage);
  };

  React.useEffect(() => {
    if (Object.values(columnSearch).length > 0) {
      setSearchArguments(columnSearch);
      setPage(0);
    }
  }, [columnSearch, setSearchArguments, setPage]);

  React.useEffect(() => {
    setSearchQuery(search);
    setPage(0);
  }, [search]);

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  function EnhancedTableColumns({ rows, columns, onRowClick, page, rowsPerPage }) {
    return columns.map((cel, cind) => {
      const arrivalIsToday = cel.arrival_date && isToday(new Date(cel.arrival_date));
      const departureIsToday = cel.departure_date && isToday(new Date(cel.departure_date));

      // ✅ Use solid color for all cells — to match perfectly
      let rowBgColor = '#f8f8ff'; // default (no highlight)
      if (arrivalIsToday) rowBgColor = '#C2F1EA';     // solid green
      else if (departureIsToday) rowBgColor = '#FFD4D1'; // solid red

      return (
        <TableRow
          hover
          key={cel.id ?? cind.toString()}
          onClick={() => {
            const dataIndex = page * rowsPerPage + cind;
            onRowClick?.(cel, dataIndex);
          }}
          style={{ height: cel['height'] ?? 40 }}
          sx={{
            display: 'flex',
            cursor: 'pointer',
            position: 'relative',
            minWidth: "100vw",
            backgroundColor: backgroundColor,
            '&:hover': {
              backgroundColor: '#E1EFFF!important', // 🔵 Set hover color here
            },
          }}
        >
          {rows.map((ele, ind) => {
            const isSticky = ind === 0 || ind === 1;
            const stickyLeft =
              ind === 0
                ? 0
                : rows[0]?.minWidth || (isMobile ? '80px' : '120px');

            return (
              <TableCell
                key={ind}
                align="center"
                sx={{
                  position: isSticky ? 'sticky' : 'relative',
                  left: isSticky ? stickyLeft : 'auto',
                  zIndex: isSticky ? 5 : 1,
                  color: 'black',
                  flex: ele.flex || 1,
                  minWidth: ele.minWidth || (isMobile ? '80px' : '120px'),
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: ele.align || 'center',
                  textAlign: ele.align || 'center',
                  borderBottom: cel['borderBottom'] || '1px solid rgba(224, 224, 224, 1)',

                  backgroundColor: rowBgColor, // ✅ same solid color for ALL cells

                  ...(ind === 0 && {
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  }),
                  ...(ind === rows.length - 1 && {
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                  }),
                }}
              >
                {cel[ele.accessor] || ''}
              </TableCell>
            );
          })}
        </TableRow>
      );
    });
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const searchList = () => {
    let temp = [...data.columns];
    if ((data.columns.length > 0) && data.columns[0].searchkey) {
      temp = [...data.columns]
        .filter(item =>
          item.searchkey?.toLowerCase().includes(searchQuery.toLowerCase()))
    }
    if (Object.keys(searchArguments).length > 0) {
      for (let ks of Object.keys(searchArguments)) {
        temp = [...temp].filter(item =>
          item[ks]?.toLowerCase().includes(searchArguments[ks].toLowerCase()))
      }
    }
    return temp;
  }

  const visibleRows = React.useMemo(
    () =>
      searchList()
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [data.columns, order, orderBy, page, rowsPerPage, searchQuery, searchArguments],
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - searchList().length) : 0;

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'auto',
      display: 'flex',
      flexDirection: 'column',
      // minHeight: '100%'
    }}>
      <Paper elevation={disableElevation ? 0 : 2} sx={{
        mb: 2,
        borderRadius: disableElevation ? 0 : 2,
        minWidth: '100%',
        // overflowX: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: "#f8f8ff!important", // Match your table background

      }}>
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            backgroundColor: "#f8f8ff!important",
            overflowX: 'auto', // enable horizontal scroll
            '&::-webkit-scrollbar': {
              height: 10,

            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: 2,
            },
          }}
        >
          <Table
            aria-labelledby="tableTitle"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              minWidth: '100%',
            }}
            stickyHeader
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.columns.length}
              headCells={data.rows}
            />
            <TableBody>
              {visibleRows.length > 0 ? (
                <EnhancedTableColumns
                  rows={data.rows}
                  columns={visibleRows}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onRowClick={onRowClick}
                />
              ) : (
                <TableRow>
                  <TableCell colSpan={data.rows.length} align="center" sx={{ backgroundColor: backgroundColor }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px', fontFamily: 'Nunito Sans' }}>
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {(emptyRows > 0) && (paginate) && (
                <TableRow
                  style={{
                    height: (40) * emptyRows,
                    '&:hover': {
                      backgroundColor: '#E1EFFF!important', // 🔵 Set hover color here
                    },
                  }}
                >
                  <TableCell colSpan={6} sx={{ backgroundColor: backgroundColor }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2} mb={2}>
          {paginate &&
            <CustomPagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={searchList().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1
              }}
            />}
        </Box>

      </Paper>
    </Box>
  );
}
